import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface IslemLog {
  id: string;
  created_at: string;
  kullanici_id: string;
  kullanici_adi: string;
  islem_turu: string;
  kayit_turu: string;
  kayit_id: string;
  aciklama: string;
  eski_deger: any;
  yeni_deger: any;
}

export default function IslemLoglariPage() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState<IslemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterIslem, setFilterIslem] = useState('tumu');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('sadem_islem_loglari')
          .select('*')
          .order('created_at', { ascending: false });

        if (filterIslem !== 'tumu') {
          query = query.eq('islem_turu', filterIslem);
        }

        const { data, error } = await query;
        if (error) throw error;
        setLogs(data || []);
      } catch {
        // Sessiz hata
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [filterIslem]);

  if (!isAdmin) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i className="ri-shield-cross-line text-red-500 text-2xl"></i>
        </div>
        <p className="text-sm text-gray-700 font-medium">Bu sayfaya erişim yetkiniz bulunmuyor.</p>
        <p className="text-xs text-gray-400 mt-1">Yalnızca Yönetici rolü erişebilir.</p>
      </div>
    );
  }

  const islemTurleri = [...new Set(logs.map(l => l.islem_turu))];
  const filteredLogs = logs;
  const totalPages = Math.ceil(filteredLogs.length / pageSize);
  const paginatedLogs = filteredLogs.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">İşlem Logları</h2>
          <p className="text-sm text-gray-500 mt-0.5">Tüm sistem işlemlerinin kaydı — salt okunur</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-shield-check-line"></i>
          <span>Yalnızca Yönetici</span>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => { setFilterIslem('tumu'); setPage(1); }}
          className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap
            ${filterIslem === 'tumu' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
          Tümü ({logs.length})
        </button>
        {islemTurleri.map(t => (
          <button key={t} onClick={() => { setFilterIslem(t); setPage(1); }}
            className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap
              ${filterIslem === t ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
          <p className="text-sm text-gray-500 mt-3">İşlem kayıtları yükleniyor...</p>
        </div>
      ) : paginatedLogs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-list-3-line text-2xl text-slate-400"></i>
          </div>
          <p className="text-sm text-gray-500 font-medium">Henüz işlem kaydı bulunmuyor.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tarih</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Kullanıcı</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Kayıt Türü</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Açıklama</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedLogs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleDateString('tr-TR')} {new Date(log.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-700">{log.kullanici_adi || 'Sistem'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded-full font-medium whitespace-nowrap">{log.islem_turu}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-gray-500">{log.kayit_turu}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-gray-500 truncate max-w-[200px] block">{log.aciklama || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{filteredLogs.length} kayıttan {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredLogs.length)} arası</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-gray-600">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold cursor-pointer transition-colors
                  ${p === page ? 'bg-sky-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-gray-600">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}